import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, CreditCard, BadgeCheck, Loader2 } from "lucide-react";

const fmt = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

export const SubscriptionModal = ({ org, onClose }) => {
  const [data, setData] = useState(null);
  const [cycle, setCycle] = useState("monthly");
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get(`/billing/subscription/${org.id}`)
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Could not load subscription"));
  }, [org.id]);

  const checkout = async (planId) => {
    setBusy(planId);
    try {
      const { data: res } = await api.post("/billing/checkout", {
        plan_id: planId, cycle, org_id: org.id, origin_url: window.location.origin,
      });
      window.location.href = res.url; // Stripe-hosted checkout
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBusy("");
    }
  };

  const sub = data?.subscription;
  const active = sub?.status === "active";

  return (
    <div className="fixed inset-0 bg-zinc-900/40 z-50 flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto" onClick={onClose}>
      <div className="brut-card max-w-3xl w-full my-4" onClick={(e) => e.stopPropagation()} data-testid="subscription-modal">
        <div className="flex items-start justify-between px-5 pt-5 pb-3 border-b border-zinc-200">
          <div>
            <div className="label-mono">SUBSCRIPTION · {org.name}</div>
            <h2 className="font-heading text-2xl font-black">Choose a plan</h2>
            {active ? (
              <div className="mt-1 inline-flex items-center gap-1.5 text-xs font-mono font-bold text-[#1F5F3F] bg-[#EAF3EE] border border-[#1F5F3F] px-2 py-0.5" data-testid="current-plan-chip">
                <BadgeCheck className="w-3.5 h-3.5" />
                {sub.plan_name?.toUpperCase()} · ACTIVE · renews by {String(sub.expires_at || "").slice(0, 10)}
              </div>
            ) : (
              <div className="mt-1 text-xs font-mono text-zinc-500">No active subscription</div>
            )}
          </div>
          <button onClick={onClose} className="brut-btn-secondary p-1.5" data-testid="subscription-modal-close"><X className="w-4 h-4" /></button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div className="flex items-center gap-2" data-testid="billing-cycle-toggle">
            {["monthly", "yearly"].map((c) => (
              <button key={c} onClick={() => setCycle(c)}
                className={`px-3 py-1.5 text-xs font-mono font-bold border border-zinc-900 ${cycle === c ? "bg-zinc-900 text-white" : "bg-white hover:bg-zinc-100"}`}
                data-testid={`cycle-${c}`}>
                {c.toUpperCase()}{c === "yearly" ? " · 2 MONTHS FREE" : ""}
              </button>
            ))}
          </div>

          {!data ? (
            <div className="py-10 text-center"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {Object.entries(data.plans).map(([pid, p]) => {
                const isCurrent = active && sub.plan_id === pid;
                return (
                  <div key={pid} className={`border p-4 flex flex-col gap-2 ${pid === "professional" ? "border-[#1F5F3F] border-2 bg-[#EAF3EE]/40" : "border-zinc-300"}`} data-testid={`plan-card-${pid}`}>
                    <div className="font-heading text-lg font-black">{p.name}</div>
                    <div className="font-mono">
                      <span className="text-2xl font-black">{fmt(p[cycle])}</span>
                      <span className="text-xs text-zinc-500">/{cycle === "monthly" ? "mo" : "yr"}</span>
                    </div>
                    <ul className="text-xs text-zinc-600 space-y-1 flex-1">
                      {p.features.map((f) => <li key={f}>• {f}</li>)}
                    </ul>
                    <button
                      disabled={!!busy || isCurrent}
                      onClick={() => checkout(pid)}
                      className={`brut-btn px-3 py-2 text-xs w-full disabled:opacity-60 ${isCurrent ? "bg-zinc-200" : ""}`}
                      data-testid={`choose-plan-${pid}`}
                    >
                      {isCurrent ? "CURRENT PLAN" : busy === pid ? "REDIRECTING…" : (<><CreditCard className="w-3.5 h-3.5 inline mr-1" />{active ? "SWITCH" : "SUBSCRIBE"}</>)}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          <p className="text-[10px] font-mono text-zinc-400">
            Secure checkout by Stripe. Test mode — use card 4242 4242 4242 4242, any future expiry, any CVC.
          </p>
        </div>
      </div>
    </div>
  );
};
